import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const BINGO_GAME_OPTIONS = ["bingo_games", "bingo_10", "bingo_20"];
const BINGO_GAME_LABELS  = { bingo_games: "All Bingo Games", bingo_10: "Bingo 5 Only", bingo_20: "Bingo 10 Only" };
const EMOJI_GAME_OPTIONS = ["emoji_games", "emoji_5", "emoji_10"];
const EMOJI_GAME_LABELS  = { emoji_games: "All Emoji Games", emoji_5: "Emoji 5 Only", emoji_10: "Emoji 3 Only" };
const KENO_GAME_OPTIONS = ["keno_games", "keno_5", "keno_10"];
const KENO_GAME_LABELS  = { keno_games: "All Keno Games", keno_5: "Keno 5 Only", keno_10: "Keno 10 Only" };

const S = {
  page: { padding: "0 0 80px 0", fontFamily: "system-ui, sans-serif", background: "#f5f5f5", minHeight: "100vh" },
  header: { background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff", padding: "1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { margin: 0, fontSize: "1.1rem", fontWeight: 700 },
  back: { color: "#fff", textDecoration: "none", fontSize: "0.85rem", opacity: 0.9 },
  summary: { background: "#fff", margin: "0.75rem 1rem", borderRadius: 12, padding: "0.75rem 1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", display: "flex", gap: "1rem" },
  sumItem: { flex: 1, textAlign: "center" },
  sumLabel: { fontSize: "0.65rem", color: "#9ca3af", textTransform: "uppercase" },
  sumVal: { fontSize: "1.1rem", fontWeight: 700, color: "#374151" },
  list: { padding: "0 1rem" },
  card: { background: "#fff", borderRadius: 12, padding: "1rem", marginBottom: "0.75rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.6rem" },
  botName: { fontWeight: 700, fontSize: "0.95rem" },
  activeBadge: (on) => ({ fontSize: "0.65rem", fontWeight: 700, padding: "0.15rem 0.6rem", borderRadius: 12, background: on ? "#d1fae5" : "#fee2e2", color: on ? "#065f46" : "#991b1b" }),
  statsRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0.4rem", marginBottom: "0.6rem" },
  stat: { background: "#f8f9fa", borderRadius: 6, padding: "0.35rem 0.5rem", textAlign: "center" },
  statLabel: { fontSize: "0.6rem", color: "#9ca3af", textTransform: "uppercase" },
  statVal: { fontSize: "0.85rem", fontWeight: 700, color: "#374151" },
  meta: { fontSize: "0.72rem", color: "#6b7280", marginBottom: "0.6rem" },
  select: { width: "100%", padding: "0.4rem", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: "0.82rem", marginBottom: "0.5rem" },
  btnRow: { display: "flex", gap: "0.5rem", borderTop: "1px solid #f3f4f6", paddingTop: "0.6rem" },
  toggleBtn: (on) => ({ flex: 1, padding: "0.4rem", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.78rem", background: on ? "#fee2e2" : "#d1fae5", color: on ? "#dc2626" : "#065f46" }),
  editBtn: { flex: 1, padding: "0.4rem", borderRadius: 6, border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: 600, fontSize: "0.78rem", background: "#f9fafb" },
  editInput: { width: "100%", padding: "0.4rem 0.6rem", borderRadius: 6, border: "1px solid #d1d5db", fontSize: "0.85rem", boxSizing: "border-box", marginBottom: "0.4rem" },
  saveBtn: { flex: 2, padding: "0.4rem", borderRadius: 6, border: "none", background: "#667eea", color: "#fff", fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" },
  cancelBtn: { flex: 1, padding: "0.4rem", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", fontSize: "0.78rem" },
};

function BotManagement() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingBot, setEditingBot] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", username: "", wallet_balance: "" });
  const adminToken = localStorage.getItem("adminToken");

  useEffect(() => {
    fetchBots();
    const interval = setInterval(fetchBots, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchBots = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/bots`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setBots(response.data);
      setError("");
    } catch (err) {
      if (loading) setError("Failed to fetch bots");
    } finally {
      setLoading(false);
    }
  };

  const initializeBots = async () => {
    try {
      await axios.post(
        `${API_URL}/admin/bots/initialize`,
        {},
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        },
      );
      fetchBots();
    } catch (err) {
      setError("Failed to initialize bots");
    }
  };

  const createBotsForGame = async (gameType) => {
    try {
      const count = 8;
      const assignedGame = gameType === "bingo" ? "bingo_games" : gameType === "keno" ? "keno_games" : "emoji_games";
      
      for (let i = 0; i < count; i++) {
        const botNum = bots.length + i + 1;
        
        await axios.post(
          `${API_URL}/admin/bots`,
          {
            name: `${gameType === "bingo" ? "🎱 Bingo" : gameType === "keno" ? "🔢 Keno" : "🎲 Emoji"} Bot ${botNum}`,
            username: `${gameType}_bot_${botNum}`,
            phone: `555${String(botNum).padStart(7, "0")}`,
            telegram_id: 9000000 + botNum,
            wallet_balance: 1000,
            assigned_game: assignedGame,
            behavior_mode: "random",
          },
          {
            headers: { Authorization: `Bearer ${adminToken}` },
          },
        );
      }
      fetchBots();
      setError("");
    } catch (err) {
      setError(`Failed to create ${gameType} bots`);
    }
  };

  const resetAllBalances = async () => {
    if (!window.confirm("Reset ALL bot balances to 1000 Birr and zero out stats? This cannot be undone.")) return;
    try {
      await axios.post(
        `${API_URL}/admin/bots/reset-balances`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      fetchBots();
      setError("");
    } catch (err) {
      setError("Failed to reset bot balances");
    }
  };

  const notifyGameBackends = async () => {
    const backends = [
      "http://localhost:8001",
      "http://localhost:8002",
      "http://localhost:8003",
      "http://localhost:8004",
      "http://localhost:8006",
    ];
    await Promise.allSettled(
      backends.map(url => axios.post(`${url}/api/bots/reload`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } }).catch(() => {}))
    );
  };

  const toggleBotStatus = async (botId, currentStatus) => {
    try {
      await axios.post(
        `${API_URL}/admin/bots/${botId}/control`,
        { is_active: !currentStatus },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        },
      );
      await notifyGameBackends();
      fetchBots();
    } catch (err) {
      setError("Failed to update bot status");
    }
  };

  const assignGame = async (botId, game) => {
    try {
      await axios.post(
        `${API_URL}/admin/bots/${botId}/control`,
        { assigned_game: game },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        },
      );
      await notifyGameBackends();
      fetchBots();
    } catch (err) {
      setError("Failed to assign game to bot");
    }
  };

  const deleteBot = async (botId) => {
    if (!window.confirm("Are you sure you want to delete this bot?")) return;

    try {
      await axios.delete(`${API_URL}/admin/bots/${botId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      fetchBots();
    } catch (err) {
      setError("Failed to delete bot");
    }
  };

  const startEdit = (bot) => {
    setEditingBot(bot.id);
    setEditForm({
      name: bot.name,
      username: bot.username,
      wallet_balance: bot.wallet_balance,
    });
  };

  const cancelEdit = () => {
    setEditingBot(null);
    setEditForm({ name: "", username: "", wallet_balance: "" });
  };

  const saveEdit = async (botId) => {
    try {
      const bot = bots.find((b) => b.id === botId);
      await axios.put(
        `${API_URL}/admin/bots/${botId}`,
        {
          name: editForm.name,
          username: editForm.username,
          wallet_balance: parseFloat(editForm.wallet_balance),
          assigned_game: bot.assigned_game,
          behavior_mode: bot.behavior_mode,
        },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        },
      );
      setEditingBot(null);
      fetchBots();
    } catch (err) {
      setError("Failed to update bot");
    }
  };

  const bingoBots = bots.filter(b => b.assigned_game === "bingo_games" || b.assigned_game === "bingo_10" || b.assigned_game === "bingo_20");
  const emojiBots = bots.filter(b => b.assigned_game === "emoji_games" || b.assigned_game === "emoji_5" || b.assigned_game === "emoji_10");
  const kenoBots = bots.filter(b => b.assigned_game === "keno_games" || b.assigned_game === "keno_5");
  
  const activeBots   = bots.filter(b => b.is_active).length;
  const playingBots  = bots.filter(b => b.is_playing).length;
  const totalWinnings = bots.reduce((s, b) => s + parseFloat(b.total_winnings || 0), 0);
  const totalLosses   = bots.reduce((s, b) => s + parseFloat(b.total_losses || 0), 0);
  const netTotal      = totalWinnings - totalLosses;

  if (loading) return <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>Loading bots...</div>;
  if (error)   return <div style={{ padding: "2rem", textAlign: "center", color: "#ef4444" }}>{error}</div>;

  return (
    <div style={S.page}>
      <header style={S.header}>
        <h1 style={S.title}>🤖 Bots ({bots.length})</h1>
        <Link to="/dashboard" style={S.back}>← Back</Link>
      </header>

      <div style={S.summary}>
        <div style={S.sumItem}>
          <div style={S.sumLabel}>Active</div>
          <div style={{ ...S.sumVal, color: "#10b981" }}>{activeBots}</div>
        </div>
        <div style={S.sumItem}>
          <div style={S.sumLabel}>Playing</div>
          <div style={{ ...S.sumVal, color: "#3b82f6" }}>{playingBots}</div>
        </div>
        <div style={S.sumItem}>
          <div style={S.sumLabel}>Total Won</div>
          <div style={{ ...S.sumVal, color: totalWinnings >= 0 ? "#10b981" : "#ef4444" }}>{totalWinnings.toFixed(0)} Birr</div>
        </div>
        <div style={S.sumItem}>
          <div style={S.sumLabel}>Total Loss</div>
          <div style={{ ...S.sumVal, color: "#ef4444" }}>{totalLosses.toFixed(0)} Birr</div>
        </div>
        <div style={S.sumItem}>
          <div style={S.sumLabel}>Net Total</div>
          <div style={{ ...S.sumVal, color: netTotal >= 0 ? "#10b981" : "#ef4444" }}>
            {netTotal >= 0 ? "+" : ""}{netTotal.toFixed(0)} Birr
          </div>
        </div>
      </div>

      <div style={S.list}>
        {bots.length === 0 && (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "2rem" }}>
            No bots found.
            <br />
            <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => createBotsForGame("bingo")} style={{ padding: "0.5rem 1.2rem", borderRadius: 8, border: "none", background: "#667eea", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                🎱 Create 8 Bingo Bots
              </button>
              <button onClick={() => createBotsForGame("emoji")} style={{ padding: "0.5rem 1.2rem", borderRadius: 8, border: "none", background: "#667eea", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                🎲 Create 8 Emoji Bots
              </button>
              <button onClick={() => createBotsForGame("keno")} style={{ padding: "0.5rem 1.2rem", borderRadius: 8, border: "none", background: "#667eea", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                🔢 Create 8 Keno Bots
              </button>
              <button onClick={initializeBots} style={{ padding: "0.5rem 1.2rem", borderRadius: 8, border: "none", background: "#10b981", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                ⚙️ Initialize All (24 Bots)
              </button>
            </div>
          </div>
        )}
        
        {bots.length > 0 && (
          <>
            {/* CREATE BUTTONS */}
            <div style={{ marginBottom: "1rem", display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => createBotsForGame("bingo")} style={{ padding: "0.4rem 1rem", borderRadius: 8, border: "none", background: "#667eea", color: "#fff", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                🎱 Add 8 Bingo Bots
              </button>
              <button onClick={() => createBotsForGame("emoji")} style={{ padding: "0.4rem 1rem", borderRadius: 8, border: "none", background: "#667eea", color: "#fff", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                🎲 Add 8 Emoji Bots
              </button>
              <button onClick={() => createBotsForGame("keno")} style={{ padding: "0.4rem 1rem", borderRadius: 8, border: "none", background: "#667eea", color: "#fff", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                🔢 Add 8 Keno Bots
              </button>
              <button onClick={resetAllBalances} style={{ padding: "0.4rem 1rem", borderRadius: 8, border: "none", background: "#ef4444", color: "#fff", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                ↻ Reset All Balances
              </button>
            </div>

            {/* BINGO BOTS SECTION */}
            <div style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
              <div style={{ background: "#667eea", color: "#fff", padding: "0.75rem 1rem", borderRadius: "8px 8px 0 0", fontWeight: 700, fontSize: "0.95rem" }}>
                🎱 Bingo Bots ({bingoBots.length})
              </div>
              {bingoBots.length === 0 ? (
                <div style={{ background: "#fff", padding: "1rem", textAlign: "center", color: "#9ca3af", borderRadius: "0 0 8px 8px" }}>
                  No Bingo bots yet
                </div>
              ) : (
                <div>
                  {bingoBots.map((bot) => (
                    <div key={bot.id} style={S.card}>
                      <div style={S.cardTop}>
                        <div>
                          {editingBot === bot.id ? (
                            <>
                              <input style={S.editInput} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" />
                              <input style={S.editInput} value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} placeholder="Username" />
                              <input style={S.editInput} type="number" value={editForm.wallet_balance} onChange={e => setEditForm({ ...editForm, wallet_balance: e.target.value })} placeholder="Balance" />
                            </>
                          ) : (
                            <>
                              <div style={S.botName}>{bot.name}</div>
                              <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>@{bot.username} · TG: {bot.telegram_id || "—"}</div>
                            </>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={S.activeBadge(bot.is_active)}>{bot.is_active ? "Active" : "Inactive"}</span>
                          {bot.is_playing && <div style={{ fontSize: "0.65rem", color: "#3b82f6", fontWeight: 700, marginTop: 2 }}>▶ Playing</div>}
                        </div>
                      </div>

                      <div style={S.statsRow}>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Balance</div>
                          <div style={S.statVal}>{parseFloat(bot.wallet_balance || 0).toFixed(0)} Birr</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Played/Won</div>
                          <div style={S.statVal}>{bot.games_played}/{bot.games_won}</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Winnings</div>
                          <div style={{ ...S.statVal, color: parseFloat(bot.total_winnings) >= 0 ? "#10b981" : "#ef4444" }}>{parseFloat(bot.total_winnings || 0).toFixed(0)}</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Losses</div>
                          <div style={{ ...S.statVal, color: "#ef4444" }}>{parseFloat(bot.total_losses || 0).toFixed(0)}</div>
                        </div>
                      </div>

                      <select
                        style={S.select}
                        value={bot.assigned_game || "bingo_games"}
                        onChange={(e) => assignGame(bot.id, e.target.value)}
                      >
                        {BINGO_GAME_OPTIONS.map(g => <option key={g} value={g}>{BINGO_GAME_LABELS[g]}</option>)}
                      </select>

                      <div style={S.btnRow}>
                        {editingBot === bot.id ? (
                          <>
                            <button style={S.saveBtn} onClick={() => saveEdit(bot.id)}>💾 Save</button>
                            <button style={S.cancelBtn} onClick={cancelEdit}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button style={S.toggleBtn(bot.is_active)} onClick={() => toggleBotStatus(bot.id, bot.is_active)}>
                              {bot.is_active ? "Deactivate" : "Activate"}
                            </button>
                            <button style={S.editBtn} onClick={() => startEdit(bot)}>✏ Edit</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* EMOJI BOTS SECTION */}
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ background: "#667eea", color: "#fff", padding: "0.75rem 1rem", borderRadius: "8px 8px 0 0", fontWeight: 700, fontSize: "0.95rem" }}>
                🎲 Emoji Bots ({emojiBots.length})
              </div>
              {emojiBots.length === 0 ? (
                <div style={{ background: "#fff", padding: "1rem", textAlign: "center", color: "#9ca3af", borderRadius: "0 0 8px 8px" }}>
                  No Emoji bots yet
                </div>
              ) : (
                <div>
                  {emojiBots.map((bot) => (
                    <div key={bot.id} style={S.card}>
                      <div style={S.cardTop}>
                        <div>
                          {editingBot === bot.id ? (
                            <>
                              <input style={S.editInput} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" />
                              <input style={S.editInput} value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} placeholder="Username" />
                              <input style={S.editInput} type="number" value={editForm.wallet_balance} onChange={e => setEditForm({ ...editForm, wallet_balance: e.target.value })} placeholder="Balance" />
                            </>
                          ) : (
                            <>
                              <div style={S.botName}>{bot.name}</div>
                              <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>@{bot.username} · TG: {bot.telegram_id || "—"}</div>
                            </>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={S.activeBadge(bot.is_active)}>{bot.is_active ? "Active" : "Inactive"}</span>
                          {bot.is_playing && <div style={{ fontSize: "0.65rem", color: "#3b82f6", fontWeight: 700, marginTop: 2 }}>▶ Playing</div>}
                        </div>
                      </div>

                      <div style={S.statsRow}>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Balance</div>
                          <div style={S.statVal}>{parseFloat(bot.wallet_balance || 0).toFixed(0)} Birr</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Played/Won</div>
                          <div style={S.statVal}>{bot.games_played}/{bot.games_won}</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Winnings</div>
                          <div style={{ ...S.statVal, color: parseFloat(bot.total_winnings) >= 0 ? "#10b981" : "#ef4444" }}>{parseFloat(bot.total_winnings || 0).toFixed(0)}</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Losses</div>
                          <div style={{ ...S.statVal, color: "#ef4444" }}>{parseFloat(bot.total_losses || 0).toFixed(0)}</div>
                        </div>
                      </div>

                      <select
                        style={S.select}
                        value={bot.assigned_game || "emoji_games"}
                        onChange={(e) => assignGame(bot.id, e.target.value)}
                      >
                        {EMOJI_GAME_OPTIONS.map(g => <option key={g} value={g}>{EMOJI_GAME_LABELS[g]}</option>)}
                      </select>

                      <div style={S.btnRow}>
                        {editingBot === bot.id ? (
                          <>
                            <button style={S.saveBtn} onClick={() => saveEdit(bot.id)}>💾 Save</button>
                            <button style={S.cancelBtn} onClick={cancelEdit}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button style={S.toggleBtn(bot.is_active)} onClick={() => toggleBotStatus(bot.id, bot.is_active)}>
                              {bot.is_active ? "Deactivate" : "Activate"}
                            </button>
                            <button style={S.editBtn} onClick={() => startEdit(bot)}>✏ Edit</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* KENO BOTS SECTION */}
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ background: "#667eea", color: "#fff", padding: "0.75rem 1rem", borderRadius: "8px 8px 0 0", fontWeight: 700, fontSize: "0.95rem" }}>
                🔢 Keno Bots ({kenoBots.length})
              </div>
              {kenoBots.length === 0 ? (
                <div style={{ background: "#fff", padding: "1rem", textAlign: "center", color: "#9ca3af", borderRadius: "0 0 8px 8px" }}>
                  No Keno bots yet
                </div>
              ) : (
                <div>
                  {kenoBots.map((bot) => (
                    <div key={bot.id} style={S.card}>
                      <div style={S.cardTop}>
                        <div>
                          {editingBot === bot.id ? (
                            <>
                              <input style={S.editInput} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" />
                              <input style={S.editInput} value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} placeholder="Username" />
                              <input style={S.editInput} type="number" value={editForm.wallet_balance} onChange={e => setEditForm({ ...editForm, wallet_balance: e.target.value })} placeholder="Balance" />
                            </>
                          ) : (
                            <>
                              <div style={S.botName}>{bot.name}</div>
                              <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>@{bot.username} · TG: {bot.telegram_id || "—"}</div>
                            </>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={S.activeBadge(bot.is_active)}>{bot.is_active ? "Active" : "Inactive"}</span>
                          {bot.is_playing && <div style={{ fontSize: "0.65rem", color: "#3b82f6", fontWeight: 700, marginTop: 2 }}>▶ Playing</div>}
                        </div>
                      </div>

                      <div style={S.statsRow}>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Balance</div>
                          <div style={S.statVal}>{parseFloat(bot.wallet_balance || 0).toFixed(0)} Birr</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Played/Won</div>
                          <div style={S.statVal}>{bot.games_played}/{bot.games_won}</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Winnings</div>
                          <div style={{ ...S.statVal, color: parseFloat(bot.total_winnings) >= 0 ? "#10b981" : "#ef4444" }}>{parseFloat(bot.total_winnings || 0).toFixed(0)}</div>
                        </div>
                        <div style={S.stat}>
                          <div style={S.statLabel}>Losses</div>
                          <div style={{ ...S.statVal, color: "#ef4444" }}>{parseFloat(bot.total_losses || 0).toFixed(0)}</div>
                        </div>
                      </div>

                      <select
                        style={S.select}
                        value={bot.assigned_game || "keno_games"}
                        onChange={(e) => assignGame(bot.id, e.target.value)}
                      >
                        {KENO_GAME_OPTIONS.map(g => <option key={g} value={g}>{KENO_GAME_LABELS[g]}</option>)}
                      </select>

                      <div style={S.btnRow}>
                        {editingBot === bot.id ? (
                          <>
                            <button style={S.saveBtn} onClick={() => saveEdit(bot.id)}>💾 Save</button>
                            <button style={S.cancelBtn} onClick={cancelEdit}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button style={S.toggleBtn(bot.is_active)} onClick={() => toggleBotStatus(bot.id, bot.is_active)}>
                              {bot.is_active ? "Deactivate" : "Activate"}
                            </button>
                            <button style={S.editBtn} onClick={() => startEdit(bot)}>✏ Edit</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BotManagement;
